import { format } from "date-fns";

/** One "When" format across every ledger screen. */
export const LEDGER_WHEN_FORMAT = "MMM d · HH:mm";

/** The untruncated timestamp the hover card shows. */
export const LEDGER_WHEN_FULL_FORMAT = "EEE d MMM · HH:mm";

/** Site names truncate around here; the plot number never does. */
export const SITE_NAME_MAX_CHARS = 20;

/**
 * Rows can legitimately have no timestamp — a variation with no allocations
 * has nobody and no time attached — so a missing or unparseable value renders
 * as an empty cell rather than throwing `RangeError` out of `date-fns`.
 */
function formatIso(iso: string | null | undefined, pattern: string): string {
  if (iso == null || iso.length === 0) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return format(date, pattern);
}

export function formatLedgerWhen(iso: string | null | undefined): string {
  return formatIso(iso, LEDGER_WHEN_FORMAT);
}

export function formatLedgerWhenFull(iso: string | null | undefined): string {
  return formatIso(iso, LEDGER_WHEN_FULL_FORMAT);
}

export function truncateSiteName(
  site: string,
  maxChars = SITE_NAME_MAX_CHARS,
): string {
  if (site.length <= maxChars) {
    return site;
  }
  return `${site.slice(0, maxChars - 1).trimEnd()}…`;
}

/**
 * "Kiddlington - TF · 144" — the site gives way, the plot never does.
 *
 * A row can be scoped to a whole site rather than a plot (a site-level
 * deduction), and then the cell is the site alone: a trailing "·" would read
 * as a missing plot rather than as a deliberate scope.
 */
export function formatSitePlot(site: string, plot?: string | null): string {
  const trimmedPlot = plot?.trim() ?? "";
  const truncatedSite = truncateSiteName(site);
  return trimmedPlot.length > 0
    ? `${truncatedSite} · ${trimmedPlot}`
    : truncatedSite;
}

/** Whole-percent share, or null when there is no meaningful denominator. */
export function percentOf(amount: number, target: number): number | null {
  if (target <= 0) {
    return null;
  }
  return Math.round((amount / target) * 100);
}
